import { useState, type ReactNode } from 'react';

/**
 * Collapsible section rendering monospaced content (typically a prompt or
 * raw LLM response). Used in both run-detail cards and single-run pages.
 *
 * See docs/FeatureDesigns/operation-detail-unified-layout.md.
 */
export interface CollapsibleCodeBlockProps {
    title: string;
    /** Text to render inside the <pre>. Pass `children` instead for custom content. */
    text?: string | null;
    /** Custom rendered content (overrides `text`). */
    children?: ReactNode;
    /** Initial collapsed state. Defaults to collapsed. */
    defaultCollapsed?: boolean;
    /** Fallback text when `text` is empty/undefined. */
    emptyFallback?: string;
    /** Optional adornment shown to the right of the title (outside the toggle button). */
    titleAccessory?: ReactNode;
}

export function CollapsibleCodeBlock({
    title,
    text,
    children,
    defaultCollapsed = true,
    emptyFallback = 'Not recorded',
    titleAccessory,
}: CollapsibleCodeBlockProps) {
    const [collapsed, setCollapsed] = useState(defaultCollapsed);
    return (
        <div className="op-collapsible-box">
            <div className="section-toggle-row">
                <button className="section-toggle" onClick={() => setCollapsed(c => !c)}>
                    <span className={`toggle-icon ${collapsed ? 'collapsed' : ''}`}>▼</span>
                    <span>{title}</span>
                </button>
                {titleAccessory && <span className="section-toggle-accessory">{titleAccessory}</span>}
            </div>
            {!collapsed && (
                children !== undefined
                    ? children
                    : <pre className="code-block">{text || emptyFallback}</pre>
            )}
        </div>
    );
}
